import React, { useEffect } from 'react';
import axios from 'axios';
import API_URL from '../config/api';

const Quality = () => {
  useEffect(() => {
    // Track page view
    axios.post(`${API_URL}/api/track`, { page: 'quality', action: 'page_view' }).catch(() => {});
  }, []);

  return (
    <div className="bg-[#f8f3e8] min-h-screen py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 text-[#1f3a27]" style={{ fontFamily: 'Georgia, serif' }}>Quality & Packaging</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            At Earthly Spice Exports, quality is not just a commitment-it's our foundation. We ensure that every
            product meets the highest standards of purity, freshness, and flavor.
          </p>
        </div>

        {/* Quality Assurance */}
        <div className="bg-[#fffdf8] rounded-2xl border border-[#e7decb] shadow-lg p-8 mb-8">
          <h2 className="text-3xl font-bold mb-6 text-[#1f3a27]" style={{ fontFamily: 'Georgia, serif' }}>Quality Assurance</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold mb-4">Our Quality Standards</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-primary mr-2">✓</span>
                  <span>100% pure spices with no fillers or additives</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">✓</span>
                  <span>Rigorous quality testing at every stage</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">✓</span>
                  <span>Authentic sourcing from trusted suppliers</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">✓</span>
                  <span>Hygienic processing facilities</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">✓</span>
                  <span>Compliance with food safety regulations</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-4">Testing & Certification</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-primary mr-2">✓</span>
                  <span>Regular testing for purity and quality</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">✓</span>
                  <span>HSN Code compliance (0910 for whole spices)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">✓</span>
                  <span>Moisture content and shelf-life testing</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">✓</span>
                  <span>Microbial safety checks</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">✓</span>
                  <span>Batch tracking for quality control</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Packaging */}
        <div className="bg-[#fffdf8] rounded-2xl border border-[#e7decb] shadow-lg p-8 mb-8">
          <h2 className="text-3xl font-bold mb-6 text-[#1f3a27]" style={{ fontFamily: 'Georgia, serif' }}>Packaging</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold mb-4">Packaging Materials</h3>
              <p className="text-gray-700 mb-4">
                We use food-grade packaging materials that ensure the freshness and quality of our spices:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>• Airtight containers and bags</li>
                <li>• Moisture-resistant packaging</li>
                <li>• UV-protected materials to preserve flavor</li>
                <li>• Tamper-evident seals</li>
                <li>• Clear labeling with product information</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-4">Packaging Sizes</h3>
              <p className="text-gray-700 mb-4">
                Available in various sizes to meet your needs:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>• Retail packs (100g, 250g, 500g)</li>
                <li>• Bulk quantities (1kg, 5kg, 10kg)</li>
                <li>• Custom packaging for wholesale orders</li>
                <li>• Gift packs and sets</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Storage Guidelines */}
        <div className="bg-[#fffdf8] rounded-2xl border border-[#e7decb] shadow-lg p-8 mb-8">
          <h2 className="text-3xl font-bold mb-6 text-[#1f3a27]" style={{ fontFamily: 'Georgia, serif' }}>Storage Guidelines</h2>
          <div className="max-w-3xl mx-auto">
            <p className="text-gray-700 mb-4">
              To maintain the quality and freshness of your spices:
            </p>
            <div className="bg-[#f1eadc] border-l-4 border-[#1f3a27] p-6 rounded-2xl">
              <ul className="space-y-2 text-gray-700">
                <li>• Store in a cool, dry place away from direct sunlight</li>
                <li>• Keep spices in airtight containers after opening</li>
                <li>• Avoid exposure to moisture and heat</li>
                <li>• Use within the recommended shelf life</li>
                <li>• Label containers with purchase date for freshness tracking</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Shelf Life */}
        <div className="bg-[#fffdf8] rounded-2xl border border-[#e7decb] shadow-lg p-8">
          <h2 className="text-3xl font-bold mb-6 text-[#1f3a27]" style={{ fontFamily: 'Georgia, serif' }}>Shelf Life & Expiry</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-[#f7f1e5] rounded-xl border border-[#e7decb]">
              <h3 className="text-xl font-semibold mb-2">Whole Spices</h3>
              <p className="text-gray-600">2-3 years when stored properly</p>
            </div>
            <div className="text-center p-6 bg-[#f7f1e5] rounded-xl border border-[#e7decb]">
              <h3 className="text-xl font-semibold mb-2">Ground Spices</h3>
              <p className="text-gray-600">1-2 years when stored properly</p>
            </div>
            <div className="text-center p-6 bg-[#f7f1e5] rounded-xl border border-[#e7decb]">
              <h3 className="text-xl font-semibold mb-2">Spice Blends</h3>
              <p className="text-gray-600">1-2 years when stored properly</p>
            </div>
          </div>
          <p className="text-center text-gray-600 mt-6">
            <strong>Note:</strong> All products are labeled with manufacturing date and best before date. 
            For maximum flavor, we recommend using whole spices within 1-2 years and ground spices within 6-12 months.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Quality;
